import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import SelectAreas from "components/ui/selectors/SelectAreas";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import { useGeneratePaymentDueAgingReport } from "http-hooks/salebill/useSalebill";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";

const reportTypeOptions = [
  { value: "V", label: "Vendorwise" },
  { value: "C", label: "Customerwise" },
];

const showOutstandingOptions = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
];

const PaymentDueAging = () => {
  const navigate = useNavigate();
  const { mutateAsync: generateReport, isPending: isGenerating } = useGeneratePaymentDueAgingReport();

  const [reportType, setReportType] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [range1, setRange1] = useState("30");
  const [range2, setRange2] = useState("45");
  const [range3, setRange3] = useState("60");
  const [range4, setRange4] = useState("75");
  const [showOutstanding, setShowOutstanding] = useState(showOutstandingOptions[0]);
  const [selectedArea, setSelectedArea] = useState(null);

  const showAreaFilter = reportType?.value === "V";

  const { data: { data: areaCustomers } = {} } = useAllPartySupMasts(
    {
      type: "C",
      level: 0,
      grplevel: 0,
      ag_status: 1,
      ...(selectedArea?.areaId ? { areaId: selectedArea.areaId } : {}),
      noPagination: true,
    },
    showAreaFilter && !!selectedArea
  );

  useEffect(() => {
    if (areaCustomers && areaCustomers.length > 0 && showAreaFilter) {
      setCustomers(areaCustomers);
    }
  }, [areaCustomers, showAreaFilter]);

  const handleAreaChange = (area) => {
    setSelectedArea(area);
    if (!area) {
      setCustomers([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formData = {
      reportType: reportType?.value,
      vendor: reportType?.value === "V" ? vendor?.ps_Id : null,
      customer: reportType?.value === "C" ? customer?.ps_Id : null,
      vendors: reportType?.value === "C" ? vendors.map(v => v.ps_Id) : [],
      customers: reportType?.value === "V" ? customers.map(c => c.ps_Id) : [],
      day1: parseInt(range1),
      day2: parseInt(range2),
      day3: parseInt(range3),
      day4: parseInt(range4),
      show_outstanding: showOutstanding?.value,
    };

    const data = await generateReport(formData);
    
    if (data?.htmlUrl) {
      const { htmlUrl, pdfUrl } = getDocumentUrls(data);
      
      const route = getDocumentViewerRoute('aging_report', {
        htmlUrl,
        pdfUrl,
        documentNumber: 'Payment Due Aging',
        documentType: 'Aging Report'
      });
      
      navigate(route);
    }
  };

  const isFormValid = () => {
    if (!reportType) return false;
    if (reportType.value === "V" && !vendor) return false;
    if (reportType.value === "C" && !customer) return false;
    if (!range1 || !range2 || !range3 || !range4) return false;
    return true;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Debitors Age Analysis Generator
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Generate payment due aging report for vendors or customers
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleSubmit}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              {/* Report Type */}
              <div className="sm:col-span-3">
                <Select
                  options={reportTypeOptions}
                  value={reportType}
                  onChange={(selected) => {
                    setReportType(selected);
                    setVendor(null);
                    setVendors([]);
                    setCustomer(null);
                    setCustomers([]);
                    setSelectedArea(null);
                  }}
                  label="Report Type"
                  placeholder="Choose Here"
                  isClearable
                  isRequired
                />
              </div>

              {/* Show Outstanding Receipt */}
              <div className="sm:col-span-3">
                <Select
                  options={showOutstandingOptions}
                  value={showOutstanding}
                  onChange={setShowOutstanding}
                  label="Show Outstanding Receipt"
                />
              </div>

              {/* Area Filter - only for Vendorwise (multi-customer) */}
              {showAreaFilter && (
                <div className="sm:col-span-3">
                  <SelectAreas
                    value={selectedArea}
                    onChange={handleAreaChange}
                    label="Area (optional)"
                    placeholder="Select area to auto-fill customers"
                    showAddButton={false}
                    isClearable={true}
                  />
                </div>
              )}

              {/* Vendor Selection */}
              {reportType && (
                <div className="sm:col-span-3">
                  <SelectVendors
                    value={reportType.value === "V" ? vendor : vendors}
                    onChange={(selected) => {
                      if (reportType.value === "V") {
                        setVendor(selected);
                      } else {
                        setVendors(selected);
                      }
                    }}
                    label="Vendor"
                    isMulti={reportType.value === "C"}
                    isRequired={reportType.value === "V"}
                    placeholder="Choose Here"
                  />
                </div>
              )}

              {/* Customer Selection */}
              {reportType && (
                <div className="sm:col-span-3">
                  <SelectCustomers
                    value={reportType.value === "C" ? customer : customers}
                    onChange={(selected) => {
                      if (reportType.value === "C") {
                        setCustomer(selected);
                      } else {
                        setCustomers(selected);
                      }
                    }}
                    label="Customer"
                    isMulti={reportType.value === "V"}
                    isRequired={reportType.value === "C"}
                    placeholder="Choose Here"
                  />
                </div>
              )}

              {/* Range 1 */}
              <div className="sm:col-span-3">
                <Input
                  type="number"
                  label="Range 1 (Days)"
                  value={range1}
                  onChange={(e) => setRange1(e.target.value)}
                  isRequired
                />
              </div>

              {/* Range 2 */}
              <div className="sm:col-span-3">
                <Input
                  type="number"
                  label="Range 2 (Days)"
                  value={range2}
                  onChange={(e) => setRange2(e.target.value)}
                  isRequired
                />
              </div>

              {/* Range 3 */}
              <div className="sm:col-span-3">
                <Input
                  type="number"
                  label="Range 3 (Days)"
                  value={range3}
                  onChange={(e) => setRange3(e.target.value)}
                  isRequired
                />
              </div>

              {/* Range 4 */}
              <div className="sm:col-span-3">
                <Input
                  type="number"
                  label="Range 4 (Days)"
                  value={range4}
                  onChange={(e) => setRange4(e.target.value)}
                  isRequired
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              disabled={!isFormValid() || isGenerating}
              className={`rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                isFormValid() && !isGenerating ? "bg-indigo-600 hover:bg-indigo-500" : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {isGenerating ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentDueAging;
